/**
 * 重点词汇API服务 - 通过后端调用Dictionary API并缓存结果
 */

import { API_BASE_URL } from '../config/api';

// 词汇响应接口（对应后端VocabularyResponse）
export interface VocabularyApiResponse {
  word: string;
  phonetic: string | null;
  definition: string;
  part_of_speech: string | null;
  difficulty_level: string;
  examples: string[];
  synonyms: string[];
  pronunciation_audio_url: string | null;
  query_count: number;
  cached: boolean;
}

// 词汇统计响应接口
export interface VocabularyStatsResponse {
  total_words: number;
  top_queried: VocabularyStats[];
  recent_additions: VocabularyStats[];
  difficulty_distribution: DifficultyDistribution;
}

export interface VocabularyStats {
  word: string;
  query_count: number;
  last_queried_at: string;
}

export interface DifficultyDistribution {
  basic: number;
  intermediate: number;
  advanced: number;
  expert: number;
}

class VocabularyApiService {
  // 移除缓存，使用最简单直接的方式

  /**
   * 获取词汇详细信息（含音标、释义等）- 直接调用后端API
   */
  async getVocabulary(word: string): Promise<VocabularyApiResponse> {
    const wordKey = word.toLowerCase().trim();

    try {
      console.log('🌐 调用后端词汇API:', wordKey);
      
      const response = await fetch(`${API_BASE_URL}/api/vocabulary/${encodeURIComponent(wordKey)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(15000), // 15秒超时
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const vocabularyData: VocabularyApiResponse = await response.json();
      
      console.log('✅ 词汇API调用成功:', {
        word: vocabularyData.word,
        phonetic: vocabularyData.phonetic,
        cached: vocabularyData.cached,
        query_count: vocabularyData.query_count
      });

      return vocabularyData;

    } catch (error) {
      console.error('❌ 词汇API调用失败:', error);
      
      // 返回基础词汇信息作为降级
      return this.createFallbackVocabulary(wordKey);
    }
  }

  /**
   * 批量获取词汇信息 - 简化为串行请求避免API负载
   */
  async getBatchVocabulary(words: string[]): Promise<VocabularyApiResponse[]> {
    const uniqueWords = [...new Set(words.map(w => w.toLowerCase().trim()))];
    
    console.log('📚 批量获取词汇:', uniqueWords);

    const results: VocabularyApiResponse[] = [];

    // 串行请求，简单直接
    for (const word of uniqueWords) {
      try {
        const vocabularyData = await this.getVocabulary(word);
        results.push(vocabularyData);
        
        // 添加小延迟避免API限制
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.warn(`批量查询失败: ${word}`, error);
        results.push(this.createFallbackVocabulary(word));
      }
    }

    return results;
  }

  /**
   * 获取词汇统计信息
   */
  async getVocabularyStats(): Promise<VocabularyStatsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/vocabulary-stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('获取词汇统计失败:', error);
      
      // 返回默认统计
      return {
        total_words: 0,
        top_queried: [],
        recent_additions: [],
        difficulty_distribution: {
          basic: 0,
          intermediate: 0,
          advanced: 0,
          expert: 0,
        },
      };
    }
  }

  /**
   * 获取热门词汇列表
   */
  async getPopularVocabulary(): Promise<VocabularyApiResponse[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/vocabulary/popular`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('获取热门词汇失败:', error);
      return [];
    }
  }

  /**
   * 触发词汇表清理（保持1万词限制）
   */
  async cleanupVocabulary(): Promise<{ success: boolean; deleted_words: number }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/vocabulary/cleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('词汇清理失败:', error);
      return { success: false, deleted_words: 0 };
    }
  }

  /**
   * 格式化词汇信息为显示用的字符串
   */
  formatVocabularyForDisplay(vocab: VocabularyApiResponse): string {
    // 音标数据调试日志（可选）
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 formatVocabularyForDisplay 词汇数据:', {
        word: vocab.word,
        phonetic: vocab.phonetic,
        phoneticExists: !!vocab.phonetic
      });
    }
    
    const phonetic = vocab.phonetic && vocab.phonetic.trim() !== '' 
      ? ` ${vocab.phonetic}` 
      : ''; // 如果没有音标就不显示，不生成fallback
    const partOfSpeech = vocab.part_of_speech ? ` (${vocab.part_of_speech})` : '';
    
    return `${vocab.word}${phonetic}${partOfSpeech} - ${vocab.definition}`;
  }

  // 移除本地词汇分析方法，全部使用后端API

  /**
   * 创建降级词汇信息
   */
  private createFallbackVocabulary(word: string): VocabularyApiResponse {
    return {
      word,
      phonetic: null, // 不生成fallback音标，如果后端API没有就不显示
      definition: `${word}的基本含义`,
      part_of_speech: null,
      difficulty_level: 'intermediate',
      examples: [`Example with ${word}.`],
      synonyms: [],
      pronunciation_audio_url: null,
      query_count: 1,
      cached: false,
    };
  }

  /**
   * 分析文本中的重点词汇 - 调用后端专门的分析API
   * v1.5.5 - 完全移除本地词汇处理，纯后端API架构
   */
  async analyzeTextVocabulary(text: string): Promise<VocabularyApiResponse[]> {
    try {
      console.log('🎯 v1.5.5 纯后端API - 调用词汇分析:', text.substring(0, 50) + '...');
      
      const response = await fetch(`${API_BASE_URL}/api/vocabulary/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text }),
        signal: AbortSignal.timeout(20000), // 20秒超时
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const analysisResult: {
        vocabulary: VocabularyApiResponse[];
        word_count: number;
        meaningful_words: string[];
      } = await response.json();
      
      console.log('✅ 文本分析API成功:', {
        word_count: analysisResult.word_count,
        meaningful_words: analysisResult.meaningful_words,
        vocabulary_count: analysisResult.vocabulary.length
      });

      return analysisResult.vocabulary;

    } catch (error) {
      console.error('❌ 文本分析API失败:', error);
      return [];
    }
  }
}

// 导出单例实例
const vocabularyApiService = new VocabularyApiService();
export default vocabularyApiService;